import { Box, Icon, IconProps, SxProps } from "@outschool/backpack";
import { faSpinner } from "@outschool/icons";
import React, { useEffect, useState } from "react";

export const LoadingIcon = (
  props: Omit<IconProps, "icon" | "fixedWidth" | "spin">,
) => <Icon icon={faSpinner} fixedWidth spin {...props} />;

export interface LoadingProps {
  /** Delay in milliseconds before the loader will show. */
  delay?: number;
  /** Expands the container around the spinner to 100vh. */
  fullHeight?: boolean;
  /** Absolutely centers the spinner so it does not affect document flow. */
  absoluteCenter?: boolean;
  sx?: SxProps;
  iconSx?: SxProps;
  [prop: string]: any;
}

/**
 * A basic loading spinner.
 */
const Loading: React.FC<LoadingProps> = ({
  delay,
  fullHeight = false,
  absoluteCenter = false,
  sx,
  iconSx,
  ...rest
}) => {
  const [show, setShow] = useState(delay ? false : true);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (delay) {
      timeoutId = setTimeout(() => setShow(true), delay);
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      // @ts-ignore
      sx={[
        {
          color: "#AAA",
          textAlign: "center",
          marginY: "3em",
          ...(fullHeight && {
            height: "100vh",
          }),
          ...(absoluteCenter && {
            position: "relative",
            boxSizing: "border-box",
            marginY: 0,
            overflow: "hidden",
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {show && (
        <Box
          sx={[
            {
              height: 100,
              width: 100,
              margin: absoluteCenter ? undefined : "auto",
              ...(absoluteCenter && {
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }),
              ...sx,
            },
            {
              display: "inline-block",
              animation: "spin 1s linear infinite",
              transformOrigin: "center",
            },
            ...(Array.isArray(iconSx) ? iconSx : [iconSx]),
          ]}
          className={"spinner"}
        >
          <style>
            {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
          </style>
          <svg
            width={100}
            height={100}
            viewBox="0 0 660 355"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_319_3766)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M372.5 35.9902C409 9.99023 438.5 -0.00976562 480 -0.00976562C581 -0.00976562 660 77.9902 660 177.49C660 276.99 581 354.99 480 354.99C438.5 354.99 409 344.99 372.5 318.99C353 304.49 342.5 299.99 330 299.99C318 299.99 307.5 304.49 287.5 318.99C250.5 344.99 221 354.99 180 354.99C79 354.99 0 276.99 0 177.49C0 77.9902 79 -0.00976562 180 -0.00976562C221.5 -0.00976562 251 9.99023 287.5 35.9902C308 49.9902 318.5 54.9902 330 54.9902C339.5 54.9902 350.5 49.9902 372.5 35.9902ZM395 226.49C421 245.99 456 259.99 479.5 259.99C523 259.99 557.5 223.49 557.5 177.49C557.5 131.49 523 94.9902 479.5 94.9902C456 94.9902 421 108.99 395 128.49C370.5 146.99 351 154.99 330 154.99C310.5 154.99 290.5 146.99 265 127.99C239 108.49 204 94.9902 180.5 94.9902C137 94.9902 102.5 131.49 102.5 177.49C102.5 223.49 137 259.99 180.5 259.99C204 259.99 239 246.49 265 226.99C289.5 207.99 309.5 199.99 330 199.99C351 199.99 370.5 207.99 395 226.49Z"
                fill="#4B01D4"
              />
            </g>
          </svg>
        </Box>
      )}
    </Box>
  );
};

export const LoadingModal = () => <Loading sx={{ flexGrow: 1 }} />;

export const LoadingInline = () => <Loading sx={{ marginY: "1em" }} />;

export default Loading;
